import { useMemo, useState } from "react";
import Checkbox from "../../components/ui/Checkbox.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import {
    managedConfigFleetCategory,
    releaseEndpointConfirmed,
    releaseEndpointExcluded,
} from "../../lib/managedConfigClient.js";
import ChangeList from "./ChangeList.jsx";
import { EndpointSearchTable, useEndpointSearch } from "./ChoosePilotEndpoints.jsx";
import { StatusMark } from "./controls.jsx";
import { formatDate, formatAuthor } from "./format.js";
import { pilotProgress, plural } from "./StepStrip.jsx";
import { INPUT_CLASS, PRIMARY_ACTION, ROW_ACTION, SECONDARY_ACTION } from "./styles.js";

function generationAtLeast(value, target) {
    try { return BigInt(value || "0") >= BigInt(target || "0"); }
    catch { return false; }
}

/// What one pilot row can show. "Your check" stays unavailable until the
/// endpoint reports the pilot generation and the time it applied it; an Origin
/// Agent too old to send the time never leaves "waiting-apply-time".
export function pilotRowStatus(endpoint, state, pilotGeneration) {
    if (releaseEndpointExcluded(endpoint)) return "excluded";
    if (!state?.reported || !generationAtLeast(state.appliedGeneration, pilotGeneration)) return "waiting-delivery";
    if (!state.appliedAt) return "waiting-apply-time";
    return "applied";
}

/// Why "Sign and release to tenant" is unavailable, in one line, or null. The
/// first three mirror the server's rule; the last is the signer's attestation.
export function signBlockReason(release, attested) {
    const { total, confirmed, unresolved } = pilotProgress(release);
    if (!total) return "Add a pilot endpoint to continue.";
    if (unresolved) return `Confirm or exclude ${plural(unresolved, "more pilot endpoint", "more pilot endpoints")} to continue.`;
    if (!confirmed) return "Confirm at least one pilot endpoint to continue.";
    if (!attested) return "Check the statement above to sign.";
    return null;
}

function earliestApply(states) {
    const times = states.map((state) => Date.parse(state.appliedAt || "")).filter(Number.isFinite);
    return times.length ? new Date(Math.min(...times)).toISOString() : null;
}

function ExcludeForm({ onSubmit, onCancel, busy }) {
    const [reason, setReason] = useState("");
    return (
        <div className="flex flex-wrap items-end gap-[6px]">
            <label className="grid min-w-[240px] flex-1 gap-[4px]">
                <span className="text-[10px] font-medium text-[color:var(--ink)]">Reason for exclusion</span>
                <input className={INPUT_CLASS} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && reason.trim()) onSubmit(reason.trim()); }} />
            </label>
            <button type="button" className={SECONDARY_ACTION} disabled={busy || !reason.trim()} onClick={() => onSubmit(reason.trim())}>Exclude endpoint</button>
            <button type="button" className={SECONDARY_ACTION} onClick={onCancel}>Cancel</button>
        </div>
    );
}

function PilotRow({ endpoint, state, pilotGeneration, agentLabel, canManage, busy, onConfirm, onExclude, onRestore }) {
    const [excluding, setExcluding] = useState(false);
    const status = pilotRowStatus(endpoint, state, pilotGeneration);
    const excluded = status === "excluded";
    const confirmed = releaseEndpointConfirmed(endpoint);
    const category = managedConfigFleetCategory(state?.category);
    const name = state?.hostname || endpoint.endpointId;
    const applied = status === "applied" || status === "waiting-apply-time" || (excluded && state?.appliedAt);
    const silent = status === "applied" && !confirmed && state.agentEventsSinceApply === 0;
    // Heartbeat times are exact; the apply is reported separately in endpoint metadata.
    const heartbeatSinceApply = Boolean(state?.lastHeartbeatAt && state?.appliedAt)
        && Date.parse(state.lastHeartbeatAt) >= Date.parse(state.appliedAt);
    const cell = "px-[10px] py-[8px] align-top";
    return (
        <>
            <tr className={`border-t border-[color:var(--line)] ${excluded ? "bg-[#aa742f]/10" : ""}`}>
                <td className={`${cell} ${excluded ? "text-[color:var(--muted)] line-through" : "text-[color:var(--ink)]"}`} title={endpoint.endpointId}>{name}</td>
                <td className={`${cell} text-[color:var(--muted)]`}>{state?.os}</td>
                <td className={`${cell} text-[color:var(--muted)]`}>{state?.agentVersion}</td>
                <td className={`${cell} whitespace-nowrap`}>{status === "waiting-delivery" || status === "waiting-apply-time" ? <span className="text-[color:var(--muted)]">Waiting for delivery</span> : category ? <StatusMark tone={category.tone}>{category.label}</StatusMark> : null}</td>
                {applied && state?.appliedAt ? (
                    <>
                        <td className={`${cell} whitespace-nowrap text-center ${heartbeatSinceApply ? "text-[color:var(--muted)]" : "text-[#8a5a23]"}`}>{heartbeatSinceApply ? formatDate(state.lastHeartbeatAt) : "None since apply"}</td>
                        <td className={`${cell} text-center ${silent ? "text-[#8a5a23]" : ""}`}>{state.agentEventsSinceApply.toLocaleString()}</td>
                    </>
                ) : (
                    <>
                        <td className={`${cell} text-center text-[color:var(--muted)]`}>-</td>
                        <td className={`${cell} text-center text-[color:var(--muted)]`}>-</td>
                    </>
                )}
                <td className={cell}>
                    <div className="grid justify-items-start gap-[4px]">
                        {excluded ? <span className="text-[#8a5a23]">Excluded</span>
                            : status === "applied" || confirmed
                                ? canManage
                                    ? <Checkbox checked={confirmed} disabled={busy} onChange={() => onConfirm(endpoint, !confirmed)} label={<span className="text-[11px]">{confirmed ? "Confirmed" : "Confirm"}</span>} />
                                    : <span>{confirmed ? "Confirmed" : "Not confirmed"}</span>
                                : <span className="text-[color:var(--muted)]">{status === "waiting-apply-time" ? "Waiting for apply time" : "Available after apply"}</span>}
                        {canManage && !excluded && !excluding ? <button type="button" className={`${ROW_ACTION} -ml-[8px]`} onClick={() => setExcluding(true)}>Exclude</button> : null}
                    </div>
                </td>
            </tr>
            {silent ? (
                <tr><td colSpan={7} className="px-[10px] pb-[8px] text-[11px] text-[#8a5a23]">{`No ${agentLabel} events since apply. Ask the user to start a ${agentLabel} session, or exclude this endpoint.`}</td></tr>
            ) : null}
            {excluding && !excluded ? (
                <tr><td colSpan={7} className="px-[10px] pb-[10px]"><ExcludeForm busy={busy} onCancel={() => setExcluding(false)} onSubmit={(reason) => { setExcluding(false); onExclude(endpoint, reason); }} /></td></tr>
            ) : null}
            {excluded ? (
                <tr className="bg-[#aa742f]/10">
                    <td colSpan={7} className="px-[10px] pb-[10px] text-[11px] leading-[1.5] text-[#8a5a23]">
                        {`Excluded by ${formatAuthor(endpoint.excludedByEmail)} at ${formatDate(endpoint.excludedAt)}. Reason: ${endpoint.exclusionReason}. This endpoint stays on v${pilotGeneration}.`}
                        {canManage ? <> <button type="button" className="border-0 bg-transparent p-0 text-[11px] text-[color:var(--green)] underline" disabled={busy} onClick={() => onRestore(endpoint)}>Undo</button></> : null}
                    </td>
                </tr>
            ) : null}
        </>
    );
}

function AddPilotEndpoint({ existingIds, onAdd, onClose, busy }) {
    const [query, setQuery] = useState("");
    const search = useEndpointSearch(query);
    return (
        <div className="grid gap-[8px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px]" role="group" aria-label="Add pilot endpoint">
            <div className="flex items-center justify-between gap-[8px]">
                <strong className="text-[12px] font-medium text-[color:var(--ink)]">Add pilot endpoint</strong>
                <button type="button" className={SECONDARY_ACTION} onClick={onClose}>Close</button>
            </div>
            <EndpointSearchTable
                query={query}
                onQueryChange={setQuery}
                search={search}
                renderAction={(endpoint) => existingIds.has(endpoint.id)
                    ? <span className="text-[10px] text-[color:var(--muted)]">In pilot</span>
                    : <button type="button" className={SECONDARY_ACTION} disabled={busy} aria-label={`Add ${endpoint.hostname || endpoint.id}`} onClick={() => onAdd(endpoint)}>Add</button>}
            />
        </div>
    );
}

function SignPanel({ release, agentLabel, signerEmail, targetCount, busy, onSign, onClose }) {
    const [attested, setAttested] = useState(false);
    const progress = pilotProgress(release);
    const reason = signBlockReason(release, attested);
    const generation = release.version?.generation;
    return (
        <section className="grid gap-[14px] rounded-[10px] border border-[color:var(--green)] bg-[color:var(--bg)] p-[16px]" aria-label="Signature">
            <div className="flex items-center justify-between gap-[8px]">
                <span className={`${EYEBROW_CLASS} text-[color:var(--green)]`}>Signature</span>
                <button type="button" className={SECONDARY_ACTION} onClick={onClose}>Close</button>
            </div>
            <h2 className="m-0 text-[17px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">{`Sign v${generation}`}</h2>
            <dl className="m-0 grid grid-cols-[100px_minmax(0,1fr)] gap-x-[10px] gap-y-[7px] text-[12px]">
                {signerEmail ? <><dt className="text-[color:var(--muted)]">Signer</dt><dd className="m-0 break-all">{signerEmail}</dd></> : null}
                <dt className="text-[color:var(--muted)]">Agent</dt><dd className="m-0">{agentLabel}</dd>
                <dt className="text-[color:var(--muted)]">Digest</dt><dd className="m-0"><code title={release.version?.documentSha256}>{release.version?.documentSha256?.slice(0, 12)}</code></dd>
                <dt className="text-[color:var(--muted)]">Pilot result</dt><dd className="m-0">{progress.excluded ? `${progress.confirmed.toLocaleString()} confirmed, ${progress.excluded.toLocaleString()} excluded` : `${progress.confirmed.toLocaleString()} confirmed`}</dd>
                {targetCount !== null ? <><dt className="text-[color:var(--muted)]">Target</dt><dd className="m-0">{plural(targetCount, "endpoint")}</dd></> : null}
            </dl>
            <div className="border-t border-[color:var(--line)] pt-[12px]">
                <Checkbox className="items-start" checked={attested} onChange={(checked) => setAttested(checked)} label={<span className="text-[12px] leading-[1.45]">I checked that the pilot endpoints still send agent data with this policy.</span>} />
            </div>
            <button type="button" className={`${PRIMARY_ACTION} min-h-[38px]`} disabled={busy || Boolean(reason)} onClick={() => onSign()}>{busy ? "Signing..." : "Sign and release to tenant"}</button>
            {reason ? <p className="m-0 text-[11px] text-[#8a5a23]">{reason}</p> : null}
            <p className={`${NOTE_CLASS} m-0`}>{`Endpoints receive v${generation} at their next check-in.`}</p>
        </section>
    );
}

/// Steps 3 and 4: the pilot verification table, and the folded signature.
export default function PilotRelease({
    definition, release, states, statesLoading, canManage, signerEmail, targetCount, tally, pilotDocument,
    busy, onRefresh, onConfirm, onExclude, onRestore, onAdd, onAbandon, onSign,
}) {
    const [signOpen, setSignOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const [showDocument, setShowDocument] = useState(false);
    const statesById = useMemo(() => new Map(states.map((state) => [state.endpointId, state])), [states]);
    const generation = release.version?.generation ?? "0";
    const appliedAt = earliestApply(states);
    const limit = release.pilotEndpointLimit;
    const atLimit = limit > 0 && release.endpoints.length >= limit;
    const existingIds = useMemo(() => new Set(release.endpoints.map((endpoint) => endpoint.endpointId)), [release.endpoints]);
    const th = "px-[10px] py-[7px] font-normal";
    return (
        <div className="grid items-start gap-[16px] xl:grid-cols-[minmax(0,1fr)_380px]">
            <div className="grid min-w-0 gap-[12px]">
                <div className="flex flex-wrap items-start justify-between gap-[10px]">
                    <div className="grid gap-[3px]">
                        <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">Pilot endpoints</h2>
                        <p className={`${NOTE_CLASS} m-0`}>{appliedAt ? `v${generation} applied at ${formatDate(appliedAt)}. Confirm each endpoint after you see its data arrive.` : `Waiting for delivery of v${generation}.`}</p>
                    </div>
                    <button type="button" className={SECONDARY_ACTION} disabled={statesLoading} onClick={onRefresh}>{statesLoading ? "Refreshing..." : "Refresh"}</button>
                </div>
                <div className="overflow-x-auto rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)]" aria-busy={statesLoading ? "true" : "false"}>
                    <table className="w-full min-w-[820px] border-collapse text-left text-[11px]">
                        <thead className="bg-[color:var(--control-bg)] text-[12px] text-[color:var(--muted)]">
                            <tr><th className={th}>Endpoint</th><th className={th}>OS</th><th className={th}>Origin Agent</th><th className={th}>Policy</th><th className={`${th} text-center`}>Last check-in</th><th className={`${th} text-center`}>Agent events since apply</th><th className={th}>Your check</th></tr>
                        </thead>
                        <tbody>
                            {release.endpoints.map((endpoint) => (
                                <PilotRow
                                    key={endpoint.endpointId}
                                    endpoint={endpoint}
                                    state={statesById.get(endpoint.endpointId)}
                                    pilotGeneration={generation}
                                    agentLabel={definition.label}
                                    canManage={canManage}
                                    busy={busy}
                                    onConfirm={onConfirm}
                                    onExclude={onExclude}
                                    onRestore={onRestore}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
                {canManage ? (
                    <div className="flex flex-wrap items-center gap-[8px]">
                        <button type="button" className={SECONDARY_ACTION} disabled={busy || atLimit} aria-expanded={adding} onClick={() => setAdding(true)}>Add pilot endpoint</button>
                        <button type="button" className={SECONDARY_ACTION} disabled={busy} onClick={onAbandon}>Abandon pilot</button>
                        {atLimit ? <span className={NOTE_CLASS}>{`A pilot holds at most ${plural(limit, "endpoint")}.`}</span> : null}
                    </div>
                ) : null}
                {adding && !atLimit ? <AddPilotEndpoint existingIds={existingIds} busy={busy} onClose={() => setAdding(false)} onAdd={onAdd} /> : null}
            </div>
            <aside className="grid content-start gap-[12px] xl:sticky xl:top-[12px]">
                {canManage ? signOpen
                    ? <SignPanel release={release} agentLabel={definition.label} signerEmail={signerEmail} targetCount={targetCount} busy={busy} onSign={onSign} onClose={() => setSignOpen(false)} />
                    : <button type="button" className={`${PRIMARY_ACTION} min-h-[40px] w-full`} onClick={() => setSignOpen(true)}>Pilot config verified</button>
                    : null}
                <section className="grid gap-[8px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]" aria-label={`Changes in v${generation}`}>
                    <span className={EYEBROW_CLASS}>{`Changes in v${generation}`}</span>
                    <ChangeList definition={definition} tally={tally} />
                    <button type="button" className="flex justify-between border-0 border-t border-[color:var(--line)] bg-transparent pt-[10px] text-left text-[10px] text-[color:var(--green)]" aria-expanded={showDocument} onClick={() => setShowDocument((value) => !value)}>{showDocument ? "Hide policy document" : "View policy document"}</button>
                    {showDocument && definition.documentNote ? <p className={`${NOTE_CLASS} m-0`}>{definition.documentNote}</p> : null}
                    {showDocument ? <pre className="m-0 max-h-[320px] overflow-auto bg-[#171512] p-[10px] text-[9px] leading-[1.5] text-[#eee7dd]">{JSON.stringify(pilotDocument, null, 2)}</pre> : null}
                </section>
            </aside>
        </div>
    );
}
